/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-26 11:39:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';

import RecommendationType from './recommendation-type';
import General from './general';
import VisualEditor from './visual-editor';
import Placement from './placement';
import PlacementSSR from './placement-ssr';

import { AI_POWERED_RECOMMENDATION_NAME } from 'src/constants/subscription';
import { RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES as RECOMMENDATION_TYPES } from 'src/constants/recommendation';

import { routes } from 'src/routes';
import {
  enterSABFullscreen,
  exitSABFullscreen
} from 'src/integrations/shopify-app-bridge/fullscreen';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';

import { handleOpenFullscreen } from 'actions/app-integration-actions';
import * as filterDataActions from 'actions/filter-data-actions';
import * as subscriptionV3Actions from 'src/stores/actions/subscription-v3-actions';
import * as themeSetupV3Actions from 'actions/theme-setup-v3-actions';
import * as themeSettingActions from 'src/stores/actions/theme-settings-actions';
import * as recommendationAction from 'src/stores/actions/recommendation-actions';
import * as themeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';

import type { ComposedProps, State } from './new.type';

class NewWidgetItem extends Component<ComposedProps, State> {
  unsubscribeExit = undefined;

  state = {
    currentStep: 0,
    dirtyOnStep: { step_1: false, step_2: false },
    widget: { pageType: 'product-page', recommendationType: '', widgetName: '' },
    hasFilterData: false,
    selectedTheme: {},
    versionOS: 0
  };

  componentDidMount() {
    const {
      getAddonStatus,
      getDetailInvoice,
      invoice,
      getAllThemes,
      allThemes,
      checkEnableBoostCore,
      getDefaultDesignSettings
    } = this.props;
    this.handleOpenFullscreenPage();

    if (_.isEmpty(allThemes)) {
      getAllThemes();
    } else {
      this.processTheme(allThemes);
    }

    // Get detail invoice
    if (_.isEmpty(invoice) || !invoice) {
      getDetailInvoice();
    }

    getAddonStatus(AI_POWERED_RECOMMENDATION_NAME);

    // Get enable boost core on live theme
    checkEnableBoostCore({});

    getDefaultDesignSettings();
  }

  componentWillReceiveProps(nextProps) {
    const { getAllThemesLoading, defaultDesignSettingsLoading } = this.props;
    const {
      allThemes: nextAllThemes,
      getAllThemesLoading: nextGetAllThemesLoading,
      defaultDesignSettingsLoading: nextDefaultDesignSettingsLoading,
      defaultDesignSettingsLoadedSuccess,
      defaultDesignSettings
    } = nextProps;

    if (getAllThemesLoading && !nextGetAllThemesLoading) {
      this.processTheme(nextAllThemes);
    }

    if (
      defaultDesignSettingsLoading &&
      !nextDefaultDesignSettingsLoading &&
      defaultDesignSettingsLoadedSuccess
    ) {
      const { widget } = this.state;
      widget.widgetDesignSettings = defaultDesignSettings;
      this.setState({ widget });
    }
  }

  componentWillUnmount() {
    this.unsubscribeExit();
  }

  processTheme = allThemes => {
    const mainTheme = _.find(allThemes, theme => theme.role === 'main') || {};
    const { isOS20 = false } = mainTheme;
    let versionOS = isOS20 ? 2 : 1;

    this.setState({ selectedTheme: mainTheme, versionOS });
    this.getThemeSetting(mainTheme);
  };

  async getThemeSetting(selectedTheme) {
    const { getThemeSettingById, isEnableShopifyIntegration, checkThemeIntegrationStatus } =
      this.props;
    const themeId = selectedTheme.id;
    if (themeId) {
      getThemeSettingById(themeId);
      if (isEnableShopifyIntegration) {
        checkThemeIntegrationStatus(themeId);
      }
    }
  }

  render() {
    const { currentStep, dirtyOnStep, widget, selectedTheme, versionOS } = this.state;
    const { getAddonStatus, isEnableShopifyIntegration } = this.props;

    let content = <div />;
    switch (currentStep) {
      case 0: {
        // Select type
        content = (
          <RecommendationType
            widget={widget}
            handleCloseFullscreenPage={this.handleCloseFullscreenPage}
            handleSelectPageType={this.handleSelectPageType}
            handleSelectRecommendationType={this.handleSelectRecommendationType}
            currentStep={currentStep}
            dirtyOnStep={dirtyOnStep}
            updateState={this.updateState}
            getAddonStatus={getAddonStatus}
          />
        );
        break;
      }
      case 1: {
        // render General
        content = (
          <General
            isCreateFlow={true}
            widget={widget}
            handleCloseFullscreenPage={this.handleCloseFullscreenPage}
            currentStep={currentStep}
            dirtyOnStep={dirtyOnStep}
            updateState={this.updateState}
          />
        );
        break;
      }
      case 2: {
        // render design
        content = (
          <VisualEditor
            isCreateFlow={true}
            widget={widget}
            handleCloseFullscreenPage={this.handleCloseFullscreenPage}
            currentStep={currentStep}
            dirtyOnStep={dirtyOnStep}
            updateState={this.updateState}
            selectedTheme={selectedTheme}
          />
        );
        break;
      }
      case 3: {
        // render placement
        content = isEnableShopifyIntegration ? (
          <PlacementSSR
            isCreateFlow={false}
            handleCloseFullscreenPage={this.handleCloseFullscreenPage}
            versionOS={versionOS}
          />
        ) : (
          <Placement
            isCreateFlow={true}
            handleCloseFullscreenPage={this.handleCloseFullscreenPage}
            versionOS={versionOS}
          />
        );
        break;
      }
      default:
    }
    return (
      <div className="BC-Page BC-Page__RecommendationNewWidgetItem BC-Page__RecommendationWidgetItem">
        {content}
      </div>
    );
  }

  // Handle action opening the fullscreen
  handleOpenFullscreenPage = () => {
    const { handleOpenFullscreen } = this.props;
    this.unsubscribeExit = enterSABFullscreen(() => {
      this.backToManagementPage();
      handleOpenFullscreen(false);
    });
    handleOpenFullscreen(true);
  };

  // Handle action closing the fullscreen
  handleCloseFullscreenPage = (runExitFullscreen = true) => {
    const { handleOpenFullscreen } = this.props;
    this.backToManagementPage();
    if (runExitFullscreen) {
      exitSABFullscreen();
      handleOpenFullscreen(false);
    }
  };

  // Handle back to management page
  backToManagementPage = () => {
    const { history } = this.props;
    history.push(routes.recommendationWidgets.list());
  };

  // Handle action when clicking page type button
  handleSelectPageType = (type: string, name: string) => {
    const { widget } = this.setState;
    this.setState({
      widget: { ...widget, pageType: type, pageTypeName: name, recommendationType: '' }
    });
  };

  // Handle action when clicking recommendation type button
  handleSelectRecommendationType = (type: string, name: string) => {
    let { hasFilterData, widget } = this.state;
    if (
      !hasFilterData &&
      (type === RECOMMENDATION_TYPES['related-items'].value ||
        type === RECOMMENDATION_TYPES['frequently-bought-together'].value)
    ) {
      const { getFilterData } = this.props;
      hasFilterData = true;
      getFilterData();
    }
    widget = { ...widget, recommendationType: type, widgetName: name };

    this.setState({ widget, hasFilterData });
  };

  // Handle update state
  updateState = (fieldName, value) => {
    this.setState({ [fieldName]: value });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get current status addon sync
  getDetailInvoice: () => dispatch(subscriptionV3Actions.getDetailInvoice(ownProps.i18n)),
  getAddonStatus: addonName => dispatch(subscriptionV3Actions.getAddonStatus(addonName)),
  handleOpenFullscreen: (isOpenFullScreen: boolean) =>
    dispatch(handleOpenFullscreen(isOpenFullScreen)),
  getFilterData: () => dispatch(filterDataActions.getFilterData(ownProps.i18n, true)),
  getAllThemes: () => dispatch(themeSetupV3Actions.getAllThemes(ownProps.i18n)),
  getThemeSettingById: id =>
    dispatch(themeSettingActions.getThemeSettingById(id, ownProps.i18n, 'recommendation')),
  checkThemeIntegrationStatus: themeId =>
    dispatch(themeIntegrationAction.checkThemeIntegrationStatus(ownProps.i18n, themeId)),
  // get default design settings for widget
  getDefaultDesignSettings: () =>
    dispatch(recommendationAction.getDefaultDesignSettings(ownProps.i18n)),
  // Check enable boost core on live theme
  checkEnableBoostCore: queries =>
    dispatch(recommendationAction.checkEnableBoostCore(queries, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  return {
    invoice: _.get(state.subscriptionV3, 'invoice', {}),
    loading: _.get(state.recommendation, 'widgetCreating', false),
    success: _.get(state.recommendation, 'widgetCreatedSuccess', false),
    widgetId: _.get(state.recommendation, 'widgetId', ''),
    accessScopes: _.get(state.oauth, 'tenantApp.accessScopes', {}),
    allThemes: _.get(state.themeSetupV3, 'allThemes', []),
    getAllThemesLoading: _.get(state.themeSetupV3, 'getAllThemesLoading', false),
    defaultDesignSettingsLoading: _.get(
      state.recommendation,
      'defaultDesignSettingsLoading',
      false
    ),
    defaultDesignSettingsLoadedSuccess: _.get(
      state.recommendation,
      'defaultDesignSettingsLoadedSuccess',
      false
    ),
    defaultDesignSettings: _.get(state.recommendation, 'defaultDesignSettings', {}),
    isEnableShopifyIntegration: enableShopifyIntegration(state.oauth.tenantApp)
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(NewWidgetItem));
